import React, { useEffect, useState } from 'react';
import BEM from '@upsales/components/Utils/bemClass';
import { Card, Flex, Title, Text, Link, Icon, Loader, Progressbar } from '@upsales/components';
import T from 'Components/Helpers/translate';
import NotificationService from 'App/babel/NotificationService';

import './OutreachPlanCard.scss';

type ClientEngagementPlan = {
	id: number;
	status: 'generating' | 'ready' | 'failed' | 'cancelled' | 'discarded' | 'completed';
	summary: string | null;
	stepCount: number;
	scheduledCount: number;
	completedCount: number;
};

type Props = {
	clientId: number;
};

/**
 * "Engagement plans" — the Prospecting Agent's outreach plans for this company,
 * surfaced at the top of the account card (WEB-8617). Mirrors DashboardAiSuggestions:
 * self-fetches keyed by clientId, gated server-side behind OUTREACH_AGENT, and returns
 * null when there's nothing worth showing. Each row deep-links into the plan review
 * (the ?plan= route, WEB-7987) where the per-contact outreach steps live.
 */
const OutreachPlanCard = ({ clientId }: Props) => {
	const [plans, setPlans] = useState<ClientEngagementPlan[] | null>(null);
	const [busyId, setBusyId] = useState<number | null>(null);

	// Mark a plan done from the card (WEB-9355/9136) — clears it here without opening the
	// review; keeps the plan + its activities. The row is a nav Link, so stop the click
	// from navigating. Drops the plan locally on success.
	const markDone = async (e: React.MouseEvent, planId: number) => {
		e.preventDefault();
		e.stopPropagation();
		if (busyId !== null) return;
		setBusyId(planId);
		const res = await fetch(`/api/v2/prospectingAgentRuns/engagementPlan/${planId}/complete`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' }
		}).catch(() => null);
		setBusyId(null);
		if (!res || !res.ok) {
			NotificationService.add({
				icon: 'times',
				style: NotificationService.style.ERROR,
				title: T('prospectingAgent.completePlanFailed') || 'Could not mark the plan as done'
			});
			return;
		}
		setPlans(prev => (prev ? prev.filter(p => p.id !== planId) : prev));
	};

	useEffect(() => {
		// The Angular react-driver can mount this before AccountCtrl.account has
		// loaded, so clientId is briefly undefined — stay empty until it arrives
		// (the driver's `watch` re-renders us with the real id once it resolves).
		if (!clientId || clientId <= 0) {
			setPlans(null);
			return;
		}
		let cancelled = false;
		fetch(`/api/v2/prospectingAgentRuns/client/${clientId}/engagementPlans`)
			.then(r => r.json())
			.then(body => {
				if (cancelled) return;
				const data: ClientEngagementPlan[] = Array.isArray(body?.data)
					? body.data
					: Array.isArray(body)
					? body
					: [];
				setPlans(data);
			})
			.catch(err => {
				// Don't blank the company card on a transient failure — leave the
				// section empty and surface the cause for diagnosis.
				console.error('[OutreachPlanCard] failed to load engagement plans', err);
			});
		return () => {
			cancelled = true;
		};
	}, [clientId]);

	// Ready plans with steps, plus in-flight generations, are a useful signal on the
	// account overview. Approving a plan schedules to-dos but doesn't complete the
	// outreach, so a fully-scheduled plan stays visible (WEB-8734) — it's not "done".
	// Failed / cancelled / empty plans are noise here (managed from the Prospecting
	// Agent page). True completion tracking (hide finished plans) awaits WEB-5872.
	const visible = (plans ?? []).filter(
		plan => plan.status === 'generating' || (plan.status === 'ready' && plan.stepCount > 0)
	);

	if (!visible.length) {
		return null;
	}

	const CSS = new BEM('OutreachPlanCard');

	return (
		<div className={CSS.b()}>
			{visible.map(plan => {
				const generating = plan.status === 'generating';
				// Real progress: activities actually carried out, not merely scheduled (WEB-9355).
				const pct = plan.stepCount > 0 ? Math.round((plan.completedCount / plan.stepCount) * 100) : 0;
				// Offer "done" once the rep has actioned the plan (steps scheduled); an unapproved
				// plan is handled from the review instead.
				const canMarkDone = !generating && plan.scheduledCount > 0;
				return (
					<Link
						key={plan.id}
						href={generating ? undefined : `#/prospecting-agent?plan=${plan.id}&from=plans`}
						nostyle={true}
						aria-label={T('prospectingAgent.engagementPlan')}
						className={CSS.elem('plan').mod({ generating }).b()}
					>
						<Card className={CSS.elem('card').b()}>
							<Flex justifyContent="space-between" alignItems="center" gap={8}>
								<Flex alignItems="center" gap={8}>
									<Icon name="paper-plane" color="bright-blue" />
									<Title size="sm">{T('prospectingAgent.engagementPlan')}</Title>
								</Flex>
								<Flex alignItems="center" gap={8}>
									{canMarkDone ? (
										<button
											type="button"
											className={CSS.elem('done').b()}
											disabled={busyId === plan.id}
											onClick={e => markDone(e, plan.id)}
										>
											{T('prospectingAgent.markPlanDone') || 'Mark as done'}
										</button>
									) : null}
									{generating ? (
										<Loader size="xs" />
									) : (
										<Icon name="chevron-right" color="grey-8" className={CSS.elem('chevron').b()} />
									)}
								</Flex>
							</Flex>

							{generating ? (
								<Text size="sm" color="grey-11" space="mts">
									{T('prospectingAgent.engagementPlanGenerating')}
								</Text>
							) : (
								<div className={CSS.elem('progress').b()}>
									<Progressbar size="sm" value={pct} color="bright-blue" />
									<Text size="sm" bold={true} space="mts">
										{T('prospectingAgent.stepsDoneOfTotal', {
											done: plan.completedCount,
											total: plan.stepCount
										})}
									</Text>
								</div>
							)}

							{plan.summary ? (
								<Text
									size="sm"
									color="grey-10"
									ellipsis={true}
									space="mts"
									className={CSS.elem('summary').b()}
								>
									{plan.summary}
								</Text>
							) : null}
						</Card>
					</Link>
				);
			})}
		</div>
	);
};

export default OutreachPlanCard;
